import { Pencil, Trash2, Clock, Search, AlertTriangle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import ForecastModeBadge from './ForecastModeBadge.jsx';
import { formatCurrency, formatDate } from '../../lib/format.js';
import { useDeleteForecast } from '../../hooks/useForecastMutations.js';
import { useDeleteIndexedExpense } from '../../hooks/useIndexedExpenses.js';
import { useRevenueHypothesis } from '../../hooks/useRevenueHypothesis.js';

/**
 * Construit l'affichage du montant + fréquence/date selon le mode.
 * Pour les modes revenue et indexed, hypothesis est utilisé pour le calcul live.
 */
function buildAmountFreq(item, hypothesis) {
  const ttc = item.amount_cents_ttc ?? item.amount_cents;

  switch (item.mode) {
    case 'recurring': {
      const freq = item.frequency === 'monthly'
        ? '/mois'
        : item.frequency === 'quarterly'
        ? '/trim.'
        : item.frequency === 'annual' || item.frequency === 'yearly'
        ? '/an'
        : item.frequency
        ? `/${item.frequency}`
        : '';
      return { amount: formatCurrency(ttc), suffix: freq, extra: null };
    }
    case 'budget': {
      const annual = item.annual_cents ?? ttc ?? 0;
      const monthly = item.monthly_equivalent_cents ?? Math.round(annual / 12);
      return { amount: formatCurrency(monthly), suffix: '/mois', extra: null };
    }
    case 'one_shot': {
      const date = item.scheduled_date ?? item.due_date;
      const suffix = date ? `le ${formatDate(date)}` : '';
      return { amount: formatCurrency(ttc), suffix, extra: null };
    }
    case 'provision': {
      const monthly = item.funding_cents_monthly ?? item.monthly_equivalent_cents ?? ttc;
      return { amount: formatCurrency(monthly), suffix: '/mois', extra: null };
    }
    case 'monthly_budget': {
      const monthly = item.budget_cents_monthly ?? item.monthly_equivalent_cents ?? ttc;
      return { amount: formatCurrency(monthly), suffix: '/mois', extra: null };
    }
    case 'debt': {
      // Montre l'échéance réelle avec sa périodicité, pas un mensuel lissé :
      // « 150 € /trim. » est ce qui sort du compte, « 50 €/mois » ne sort jamais.
      const freq = { 1: '/mois', 3: '/trim.', 6: '/sem.', 12: '/an' }[item.payment_frequency_months] ?? '/mois';
      const suffix = item.next_payment_date
        ? `${freq} · prochaine le ${formatDate(item.next_payment_date)}`
        : freq;
      return { amount: formatCurrency(ttc), suffix, extra: null };
    }
    case 'revenue': {
      const baseline = hypothesis?.baseline_cents_monthly;
      const amount = baseline != null ? formatCurrency(baseline) : '—';
      return { amount, suffix: '/mois', extra: 'hypothese median' };
    }
    case 'indexed': {
      const pct = item.pct_of_revenue ?? 0;
      const baseline = hypothesis?.baseline_cents_monthly;
      if (baseline != null) {
        const estimated = Math.round(baseline * pct / 100);
        return {
          amount: `${pct} % CA`,
          suffix: `~${formatCurrency(estimated)}/mois`,
          extra: null,
        };
      }
      return {
        amount: `${pct} % CA`,
        suffix: '',
        extra: 'no-hypothesis',
      };
    }
    default:
      return { amount: formatCurrency(ttc), suffix: '', extra: null };
  }
}

/**
 * Ligne compacte d'un item prévisionnel. Hauteur cible ~32px.
 * 1 seule ligne : [Label] [badge mode] [montant + fréq] [TVA] [actions]
 *
 * Props :
 *   item               — objet item tel que renvoyé par GET /v1/forecasts/tree (ou indexed-expenses)
 *   onEdit             — callback (item) => void
 *   onDelete           — callback optionnel (item) => void — si absent, confirme via window.confirm
 *   onOpenDrawer       — callback (item) => void — pour ProvisionDetailDrawer
 *   onOpenHistory      — callback (item) => void — pour RecurrenceTransactionsPanel (mode recurring seulement)
 *   hypothesis         — objet hypothèse active (pour modes revenue et indexed) — optionnel
 */
export default function ForecastItemCompactRow({
  item,
  onEdit,
  onDelete,
  onOpenDrawer,
  onOpenHistory,
  hypothesis: hypothesisProp,
}) {
  // Pour les modes revenue et indexed, utilise la prop si fournie,
  // sinon consomme le hook directement (évite le prop-drilling depuis ForecastCategoryCard).
  // Le hook a staleTime=60s donc ne génère pas de requêtes supplémentaires.
  const needsHypothesis = item.mode === 'indexed' || item.mode === 'revenue';
  const { data: hypothesisFromHook } = useRevenueHypothesis();
  const hypothesis = hypothesisProp ?? (needsHypothesis ? hypothesisFromHook : undefined);

  const label = item.name ?? item.label ?? '—';
  const { amount, suffix, extra } = buildAmountFreq(item, hypothesis);

  // Couleur montant selon signe / mode
  const amountCents = item.amount_cents_ttc ?? item.amount_cents ?? item.monthly_equivalent_cents ?? 0;
  const isIndexedOrRevenue = item.mode === 'indexed' || item.mode === 'revenue';
  const amountColor = isIndexedOrRevenue
    ? (item.mode === 'revenue' ? 'text-green-700' : 'text-rose-600')
    : amountCents < 0
    ? 'text-red-600'
    : 'text-green-700';

  const deleteForecast = useDeleteForecast();
  const deleteIndexed = useDeleteIndexedExpense();
  const isDeleting = deleteForecast.isPending || deleteIndexed.isPending;

  function handleDelete() {
    if (onDelete) {
      onDelete(item);
      return;
    }
    if (!window.confirm(`Supprimer "${label}" ? Cette action est irréversible.`)) return;
    if (item.mode === 'indexed') {
      deleteIndexed.mutate(item.id);
    } else {
      deleteForecast.mutate({ mode: item.mode, id: item.id });
    }
  }

  return (
    <div className="group flex items-center gap-1.5 px-2 py-1 text-xs hover:bg-slate-50 rounded-lg transition-colors min-h-[32px]">
      {/* Label */}
      <span className="flex-1 min-w-0 font-medium text-slate-800 truncate">{label}</span>

      {/* Badge mode minuscule */}
      <CompactModeBadge mode={item.mode} />

      {/* Montant + fréquence */}
      <span className={`shrink-0 font-semibold ${amountColor} whitespace-nowrap`}>
        <span className={isIndexedOrRevenue ? '' : 'tabular-nums'}>{amount}</span>
        {suffix && (
          <span className="font-normal text-slate-400 ml-0.5">{suffix}</span>
        )}
      </span>

      {/* Warning : pas d'hypothese (mode indexed sans hypothese) */}
      {extra === 'no-hypothesis' && (
        <span
          title="Aucune hypothese de revenus configuree"
          className="shrink-0 text-amber-500"
          aria-label="Pas d'hypothese CA active"
        >
          <AlertTriangle size={11} />
        </span>
      )}

      {/* TVA */}
      {item.vat_rate != null && (
        <span className="shrink-0 text-slate-400 whitespace-nowrap">
          · {item.vat_rate} % TVA
        </span>
      )}

      {/* Action : gérer la poche (provision) */}
      {item.mode === 'provision' && onOpenDrawer && (
        <button
          type="button"
          onClick={() => onOpenDrawer(item)}
          className="shrink-0 p-1 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 opacity-100 md:opacity-0 md:group-hover:opacity-100"
          aria-label={`Gérer la poche : ${label}`}
          title="Gérer la poche"
        >
          <Search size={13} />
        </button>
      )}

      {/* Action : historique paiements (récurrence uniquement) */}
      {item.mode === 'recurring' && onOpenHistory && (
        <button
          type="button"
          onClick={() => onOpenHistory(item)}
          className="shrink-0 p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 opacity-100 md:opacity-0 md:group-hover:opacity-100"
          aria-label={`Historique paiements : ${label}`}
          title="Voir les paiements"
        >
          <Clock size={13} />
        </button>
      )}

      {/* Action : ouvrir la dette — elle se gère depuis /dettes, pas ici */}
      {item.mode === 'debt' && (
        <Link
          to={`/dettes/${item.id}`}
          className="shrink-0 p-1 rounded text-slate-400 hover:text-sky-600 hover:bg-sky-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 opacity-100 md:opacity-0 md:group-hover:opacity-100"
          aria-label={`Ouvrir la dette : ${label}`}
          title="Gérer depuis la page Dettes"
        >
          <ExternalLink size={13} />
        </Link>
      )}

      {/* Action : éditer — jamais pour un item dérivé (lecture seule) */}
      {onEdit && !item.readonly && (
        <button
          type="button"
          onClick={() => onEdit(item)}
          className="shrink-0 p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 opacity-100 md:opacity-0 md:group-hover:opacity-100"
          aria-label={`Modifier : ${label}`}
          title="Modifier"
          disabled={isDeleting}
        >
          <Pencil size={13} />
        </button>
      )}

      {/* Action : supprimer — jamais pour un item dérivé (lecture seule) */}
      {!item.readonly && (
      <button
        type="button"
        onClick={handleDelete}
        className="shrink-0 p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 opacity-100 md:opacity-0 md:group-hover:opacity-100"
        aria-label={`Supprimer : ${label}`}
        title="Supprimer"
        disabled={isDeleting}
      >
        <Trash2 size={13} />
      </button>
      )}
    </div>
  );
}

// Badge mode compact (texte encore plus petit que ForecastModeBadge standard)
const MODE_CONFIG = {
  recurring: { label: 'Récurrent', className: 'bg-blue-50 text-blue-700' },
  budget: { label: 'Budget', className: 'bg-purple-50 text-purple-700' },
  one_shot: { label: 'Ponctuel', className: 'bg-amber-50 text-amber-700' },
  provision: { label: 'Provision', className: 'bg-emerald-50 text-emerald-700' },
  revenue: { label: 'CA', className: 'bg-green-50 text-green-700' },
  indexed: { label: 'Indexé', className: 'bg-rose-50 text-rose-700' },
  monthly_budget: { label: 'Budget mens.', className: 'bg-violet-100 text-violet-700' },
};

function CompactModeBadge({ mode }) {
  const config = MODE_CONFIG[mode];
  if (!config) return null;
  return (
    <span
      className={`shrink-0 inline-block text-[9px] font-medium px-1 py-0 leading-4 rounded ${config.className}`}
    >
      {config.label}
    </span>
  );
}
